import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { ArrowUpRight } from 'lucide-react'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import SmartLink from '@/components/generals/smart-link'
import { contact, footer } from '@/constants/content'

const linkClass = 'group inline-flex items-center gap-2 transition-colors duration-200 hover:text-blaze'

const Footer: FC = (): ReactNode => {
    const wordmarkRef = useRef<HTMLDivElement>(null)
    const reduce = useReduceMotion()
    const { scrollYProgress } = useScroll({ target: wordmarkRef, offset: ['start end', 'end end'] })
    const y = useTransform(scrollYProgress, [0, 1], ['75%', '0%'])

    return (
        <footer className='bg-ink text-paper [--focus-ring:var(--paper)]'>
            <div className='shell'>
                <div className='grid gap-12 border-t border-paper/15 py-14 lg:grid-cols-12 lg:gap-8 lg:py-20'>
                    <div className='lg:col-span-5'>
                        <p className='font-heading text-3xl font-extrabold tracking-[-0.02em] wdth-85'>
                            David Mudry
                        </p>

                        <p className='mt-2 text-lg text-paper/75'>
                            {footer.tagline}
                        </p>
                    </div>

                    <nav aria-label='Footer' className='lg:col-span-3'>
                        <ul className='flex flex-col gap-3 font-heading text-xl font-semibold'>
                            {footer.links.map(link => (
                                <li key={link.href}>
                                    <SmartLink href={link.href} className={linkClass}>
                                        {link.label}
                                    </SmartLink>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <ul className='flex flex-col gap-3 text-lg lg:col-span-4'>
                        <li>
                            <a href={contact.phoneHref} className={linkClass}>
                                {contact.phone}
                            </a>
                        </li>

                        <li>
                            <a href={contact.emailHref} className={linkClass}>
                                {contact.email}
                            </a>
                        </li>

                        <li>
                            <SmartLink href={contact.linkedin} className={linkClass}>
                                {footer.linkedin}
                                <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 ease-snap group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                            </SmartLink>
                        </li>
                    </ul>
                </div>

                <div ref={wordmarkRef} aria-hidden='true' className='overflow-hidden'>
                    <motion.svg
                        viewBox='0 0 1000 214'
                        className='block h-auto w-full text-blaze'
                        style={{ y: reduce ? '0%' : y }}
                    >
                        <text
                            x='0'
                            y='166'
                            textLength='1000'
                            lengthAdjust='spacingAndGlyphs'
                            fill='currentColor'
                            style={{
                                fontFamily: 'var(--font-app-heading, var(--font-app-sans, ui-sans-serif, system-ui, sans-serif))',
                                fontSize: 206,
                                fontWeight: 800,
                                fontVariationSettings: '"wdth" 75'
                            }}
                        >
                            David Mudry
                        </text>
                    </motion.svg>
                </div>

                <div className='flex flex-col gap-3 border-t border-paper/15 py-6 text-sm text-paper/70 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        {footer.copyright}
                    </p>

                    <SmartLink href='/privacy' className='underline-offset-4 transition-colors hover:text-paper hover:underline'>
                        {footer.privacy}
                    </SmartLink>
                </div>
            </div>
        </footer>
    )
}

export default Footer
